/**
 * AudioFloatingButton.tsx — control minimalista para el audio ambiental global.
 *
 * Reemplaza a la barra sticky del EventAudioPlayer viejo. Vive en el layout
 * junto al `FloatingWhatsApp` y al `ScrollToTopButton`, en la esquina inferior
 * IZQUIERDA (opuesto al WhatsApp que está a la derecha).
 *
 * Reglas de renderizado:
 *  · SOLO aparece si `audioUrl !== null` en el context — cuando el usuario
 *    sale de la página que registró el audio, `EventoDetalle.useEffect` limpia
 *    la URL y el FAB desaparece solo, sin acoplarse al ruteo.
 *
 * Interacción:
 *  · Ícono `Volume2` cuando se oye, `VolumeX` cuando está silenciado.
 *  · Click → `toggleMute()`. La preferencia se persiste en localStorage vía
 *    el context (`ceciluz_events_audio_muted`).
 *  · Animación de "respiración" sutil cuando está sonando y sin mute —
 *    señala visualmente que hay sonido activo. Se desactiva con
 *    `prefers-reduced-motion`.
 *
 * Diseño:
 *  · Círculo compacto (h-11 w-11) con gradiente violeta noche y aro dorado —
 *    consistente con el brand kit del FloatingWhatsApp pero más discreto.
 *  · Tooltip a la derecha en hover (sm+): "Silenciar audio" / "Activar audio".
 */
import { Volume2, VolumeX } from 'lucide-react';

import { cn } from '../../lib/cn';
import { useAudioPlayer } from './AudioPlayerContext';

/** CSS de "respiración" mientras suena — mismo lenguaje visual que el
 *  FloatingWhatsApp pero más sutil (3.5s + amplitud menor). Se monta 1 vez
 *  inline para no contaminar `index.css`. `prefers-reduced-motion` la anula. */
const KEYFRAMES_CSS = `
@keyframes ceciluzAudioBreath {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 8px 20px rgba(26, 20, 38, 0.28);
  }
  50% {
    transform: scale(1.04);
    box-shadow: 0 10px 24px rgba(212, 175, 55, 0.30);
  }
}
.ceciluz-audio-btn-playing {
  animation: ceciluzAudioBreath 3.5s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .ceciluz-audio-btn-playing { animation: none; }
}
`;

export function AudioFloatingButton() {
  const { audioUrl, isPlaying, isMuted, toggleMute, label } = useAudioPlayer();

  // Fuera del contrato: si no hay URL activa, no renderizamos NADA.
  // El FAB desaparece del DOM completo cuando el usuario sale del detalle.
  if (!audioUrl) return null;

  const shouldBreathe = isPlaying && !isMuted;
  const Icon = isMuted ? VolumeX : Volume2;
  const buttonLabel = isMuted ? 'Activar audio ambiental' : 'Silenciar audio ambiental';
  const tooltip = isMuted ? 'Activar audio' : 'Silenciar audio';

  return (
    <>
      <style>{KEYFRAMES_CSS}</style>
      <div
        // Esquina inferior IZQUIERDA — opuesta al FloatingWhatsApp
        // (`bottom-6 right-6`) y al ScrollToTopButton para no solaparse.
        // `pointer-events-none` en el wrapper para que el tooltip no atrape
        // clicks fuera del botón.
        //
        // Bug QA 2026-06-26: subimos de `z-sticky` a `z-toast` para
        // garantizar visibilidad por encima del Navbar sticky, overlays
        // (drawers/modales que quedan sobre el contenido) y cualquier
        // sección con `data-dark-bg` que use su propio isolate. Sigue por
        // debajo del `z-tooltip` global para que un tooltip legítimo pueda
        // aparecer sobre el FAB sin conflictos.
        className="pointer-events-none fixed bottom-6 left-6 z-toast group"
      >
        <div className="pointer-events-auto relative flex items-center gap-2">
          <button
            type="button"
            onClick={toggleMute}
            aria-label={buttonLabel}
            aria-pressed={!isMuted}
            title={tooltip}
            className={cn(
              'inline-flex h-11 w-11 items-center justify-center rounded-pill',
              'bg-gradient-to-br from-primary-900 via-primary-800 to-primary-vivid',
              'text-cream-50 ring-2 ring-gold-500/60',
              'shadow-elev2',
              'transition-transform duration-2 ease-out',
              'hover:scale-105 active:scale-95',
              'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-gold-500',
              shouldBreathe && 'ceciluz-audio-btn-playing',
            )}
          >
            <Icon
              className={cn(
                'h-5 w-5',
                // Cuando está muteado, el ícono va dorado para atraer la
                // atención sobre "hay algo que puedes activar".
                isMuted ? 'text-gold-500' : 'text-cream-50',
              )}
              strokeWidth={1.75}
              aria-hidden="true"
            />
          </button>

          {/* Tooltip sutil a la derecha — visible sólo en hover/focus en desktop.
              En mobile con touch el `title` nativo cubre el mismo rol. */}
          <span
            className={cn(
              'hidden sm:inline-block',
              'pointer-events-none rounded-md px-3 py-1.5',
              'bg-primary-900/95 text-caption text-cream-50 shadow-elev1',
              'opacity-0 -translate-x-1 transition-all duration-2 ease-out',
              'group-hover:opacity-100 group-hover:translate-x-0',
              'group-focus-within:opacity-100 group-focus-within:translate-x-0',
            )}
            role="tooltip"
          >
            {label ? `${tooltip} — ${label}` : tooltip}
          </span>
        </div>
      </div>
    </>
  );
}
